/**
 * Front-end model representing a geometry.
 *
 * This model stores geometry internally as GeoJSON and allows for conversion back and forth between GeoJSON and the ESRI format.
 * @module app/models/map/Geometry
 */
import Backbone from "backbone";
import _ from "underscore";
import GeoUtils from "app/utils/GeoUtils";
var GeoJSONConstants = {
  Point: "Point",
  MultiPoint: "MultiPoint",
  LineString: "LineString",
  MultiLineString: "MultiLineString",
  Polygon: "Polygon",
  MultiPolygon: "MultiPolygon",
  GeometryCollection: "GeometryCollection",
};

var EsriConstants = {
  Point: "point",
  MultiPoint: "multipoint",
  LineString: "polyline",
  Polygon: "polygon",
};

/**
 * Converts the given GeoJSON geometry type to an Esri geometry type
 *
 * Note: Not all GeoJSON types are supported
 * @param {String} geoJsonType The type to convert
 * @return {String} The converted type
 */
function toEsriType(geoJsonType) {
  switch (geoJsonType) {
    case GeoJSONConstants.Point:
      return EsriConstants.Point;
    case GeoJSONConstants.MultiPoint:
      return EsriConstants.MultiPoint;
    case GeoJSONConstants.LineString:
      return EsriConstants.LineString;
    case GeoJSONConstants.MultiLineString:
      return EsriConstants.LineString;
    case GeoJSONConstants.Polygon:
      return EsriConstants.Polygon;
  }
}

/**
 * Converts the given Esri geometry type to a GeoJSON geometry type
 * @param {String} esriType The type to convert
 * @return {String} The converted type
 */
function toGeoJSONType(esriType) {
  switch (esriType) {
    case EsriConstants.Point:
      return GeoJSONConstants.Point;
    case EsriConstants.MultiPoint:
      return GeoJSONConstants.MultiPoint;
    case EsriConstants.LineString:
      return GeoJSONConstants.LineString;
    case EsriConstants.Polygon:
      return GeoJSONConstants.Polygon;
  }
}

var Geometry = Backbone.Model.extend({
  defaults: function () {
    return {
      type: GeoJSONConstants.Point,
      coordinates: [[]],
    };
  },

  /**@see http://resources.arcgis.com/en/help/arcgis-rest-api/#/Geometry_Objects/02r3000000n1000000/*/

  /**
   * Parse attributes from a given `esri/geometry/Geometry`. This will overwrite the `Type` and `pointLists` attributes.
   * @param  {esri/geometry/Geometry} mapModel The model which provides the source of the data.
   * @return {Geometry} A reference to `this`.
   */
  parseEsriGeometry: function (mapModel) {
    this.set("type", toGeoJSONType(mapModel.type));
    this.clearCoordinates(true);

    var i, j, point;

    if (mapModel.type == EsriConstants.Point) {
      this.set("coordinates", [
        mapModel.getLongitude(),
        mapModel.getLatitude(),
        0,
      ]);
    } else if (mapModel.type == EsriConstants.MultiPoint) {
      for (i = 0; i < mapModel.points.length; i++) {
        point = mapModel.getPoint(i);

        this.addPoint(0, point.getLongitude(), point.getLatitude());
      }
    } else if (mapModel.type == EsriConstants.Polygon) {
      for (i = 0; i < mapModel.rings.length; i++) {
        for (j = 0; j < mapModel.rings[i].length; j++) {
          point = mapModel.getPoint(i, j);

          this.addPoint(i, point.getLongitude(), point.getLatitude());
        }
      }
    } else if (mapModel.type == EsriConstants.LineString) {
      for (j = 0; j < mapModel.paths.length; j++) {
        for (i = 0; i < mapModel.paths[j].length; i++) {
          point = mapModel.getPoint(j, i);

          this.addPoint(j, point.getLongitude(), point.getLatitude());
        }
      }
    }

    this.trigger("change:coordinates", this, this.get("coordinates"));
    this.trigger("change", this);

    return this;
  },

  /**
   * Create an esri.Geometry instance of the geometry represented by this object.
   * @return {esri.Geometry} The geometry object, in Web Mercator units.
   */
  generateEsriGeometry: function () {
    var MapPoint = window.require("esri/geometry/Point");
    var Multipoint = window.require("esri/geometry/Multipoint");
    var Polygon = window.require("esri/geometry/Polygon");
    var Polyline = window.require("esri/geometry/Polyline");
    var SpatialReference = window.require("esri/SpatialReference");
    var webMercatorUtils = window.require("esri/geometry/webMercatorUtils");

    var sr = new SpatialReference({
      wkid: 102100,
      latestWkid: 3857,
    });
    var mapModel;
    var type = this.getGeometryType();
    var createMapPoint;
    var createMapPointBase = function (point) {
      return webMercatorUtils.lngLatToXY(point[0], point[1]);
    };

    if (type == EsriConstants.LineString) {
      createMapPoint = createMapPointBase;
    } else {
      createMapPoint = function (point) {
        return new MapPoint(createMapPointBase(point), sr);
      };
    }

    createMapPoint = _.bind(createMapPoint, this);

    if (type == EsriConstants.Point) {
      mapModel = createMapPoint(this.getCoordinateArray());
    } else if (type == EsriConstants.Polygon) {
      mapModel = new Polygon(sr);

      for (var i = 0; i < this.getCoordinateArrayCount(); i++) {
        var points = [];
        var coordArray = this.getCoordinateArray(i);

        for (var j = 0; j < coordArray.length; j++) {
          points.push(createMapPoint(coordArray[j]));
        }

        mapModel.addRing(points);
      }
    } else if (type == EsriConstants.MultiPoint) {
      mapModel = new Multipoint({
        points: _.map(
          _.filter(this.getCoordinateArray(), function (coordinatePair) {
            return !_.isEmpty(coordinatePair);
          }),
          function (coordinatePair) {
            return createMapPoint(coordinatePair);
          }
        ),
        spatialReference: sr,
      });
    } else if (type == EsriConstants.LineString) {
      var coords = this.getCoordinateArray();
      var paths = [];

      if (coords.length && _.isArray(coords[0])) {
        if (coords[0].length && _.isArray(coords[0][0])) {
          //three level array
          paths = _.map(coords, function (coords) {
            return _.map(coords, createMapPoint);
          });
        } else {
          //two level array
          paths = [_.map(coords, createMapPoint)];
        }
      }

      mapModel = new Polyline({
        paths: paths,
        spatialReference: sr,
      });
    }

    return mapModel;
  },

  /**
   * Generate a measurement of this geometry. The measurement is *not* cached.
   * @param  {esri.units} areaUnit   The unit to use for area.
   * @param  {esri.units} lengthUnit The unit to use for length.
   * @return {Object}            A hash of the measurements. For points, this is an object with a `latitude` and `longitude` field, each in degrees. For lines, this is an object with a `length` field, containing the length in `lengthUnit` units. For polygons, this is an object with an `area` field and a `perimeter` field. The area field contains the area in `areaUnit` units, and the perimeter field contains the perimeter in `lengthUnit` units. Note that the perimeter is based on the outer-most ring. For all other geometry types, the result is an empty object.
   */
  measure: function (areaUnit, lengthUnit) {
    var units = window.require("esri/units");

    return GeoUtils.measure(
      this.generateEsriGeometry(),
      areaUnit || units.SQUARE_FEET,
      lengthUnit || units.FEET
    );
  },

  /**
   * Gets the number of coordinate arrays in the model.
   * @return {Integer} The number of coordinate arrays.
   */
  getCoordinateArrayCount: function () {
    return _.size(this.get("coordinates"));
  },

  /**
   * Gets the list of points in the point list at the given index.
   * @param  {Integer} [listIdx] The point list index. Defaults to 0.
   * @return {Array}         An array of `Point` objects.
   */
  getCoordinateArray: function (listIdx) {
    if (this.get("type") != GeoJSONConstants.Polygon) {
      return this.get("coordinates");
    } else {
      return this.get("coordinates")[listIdx || 0];
    }
  },

  /**
   * Gets the center of a geometry, by generating an Esri geometry, getting the center of the geometry, and projecting it the spatial reference provided in the param.
   * @param  {Integer} [wkid] The spatial reference wkid to convert to. Defaults to 4326 if none is supplied.
   * @return {Array}         An array of `Point` objects.
   */
  getCenter: function (wkid) {
    var spatialReferenceIdentifier = wkid || 4326;
    var geometry = this.generateEsriGeometry();

    return GeoUtils.changeCoordinateSystem(
      GeoUtils.getCenter(geometry),
      spatialReferenceIdentifier
    );
  },

  /**
   * Add an empty point list to the model.
   * @return {app/models/map/Geometry} The list that was added.
   */
  addCoordinateArray: function () {
    this.get("coordinates").push([]);

    return this;
  },

  /**
   * Add a point to the model.
   * @param {Number} [listIdx] The index of the point list to which to add the point. Defaults to 0.
   * @param {Number} x The "x" coordinate (longitude) of the point to add.
   * @param {Number} y The "y" coordinate (latitude) of the point to add.
   * @param {Number} [z] The "z" coordinate (elevation) of the point to add.
   */
  addPoint: function (listIdx, x, y, z) {
    var point = [x, y, z || 0];

    listIdx = listIdx || 0;

    while (listIdx >= this.getCoordinateArrayCount()) {
      this.addCoordinateArray();
    }

    if (this.get("type") == GeoJSONConstants.LineString) {
      this.getCoordinateArray()[listIdx].push(point);
    } else {
      this.getCoordinateArray(listIdx).push(point);
    }

    return this;
  },

  /**
   * Clears all coordinates in the model.
   * @param {Boolean} [silent=false] True to prevent raising events.
   * @return {Geometry} A reference to `this`.
   */
  clearCoordinates: function (silent) {
    this.set("coordinates", [[]], {
      silent: silent,
    });

    return this;
  },

  /**
   * Gets the ESRI-supported representation of the geometry type of this model.
   * @return {string} The translated geometry type.
   */
  getGeometryType: function () {
    //translating internal representation to esri representation
    return toEsriType(this.get("type"));
  },

  /**
   * Gets the feature associated with this geometry, if any.
   * @return {app/models/Feature} The associated feature.
   */
  getFeature: function () {
    return this.options.feature;
  },

  /**
   * Gets the ID of the feature associated with this geometry, if any.
   * @return {String} The id of the associated feature, if any, or this model's cid, if there is no feature.
   */
  featureId: function () {
    if (this.getFeature()) {
      return this.getFeature().id;
    } else {
      return this.cid;
    }
  },

  sync: function () {
    return this.getFeature().sync.apply(this.getFeature(), arguments);
  },

  initialize: function (attributes, options) {
    Backbone.Model.prototype.initialize.apply(this, arguments);

    this.options = _.defaults({}, options);
    var self = this;

    Object.defineProperty(this, "id", {
      get: function () {
        return self.featureId();
      },
    });
  },

  save: function (attributes, options) {
    var layer = this.originalLayer;
    var modelReal = layer.toData(this);
    var feature = modelReal.getFeature();

    this.set(attributes, options);
    feature.set("geometry", this.toJSON());
    feature.set("dataType", layer.featureLayer.id);
    feature.collection = layer.featureLayer.getItems();

    return feature.save(null, options);
  },

  isNew: function () {
    return this.getFeature() && this.getFeature().isNew();
  },

  clone: function () {
    //think about cloning any object attributes
    var geometryClone = this.toJSON();

    delete geometryClone[this.idAttribute]; //we want clones to be "new"
    geometryClone = new this.constructor(geometryClone, {
      parse: true,
    });

    return geometryClone;
  },
});

export default Geometry;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/map/Geometry.js
// module id = 133
// module chunks = 1